import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ArrowLeft, Download, Pencil, Plus, Save, Trash2, Upload } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import api, { resolveMedia } from "@/lib/api";
import { ImageField } from "@/components/ImageField";

const fmtDate = (iso) => iso ? new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";
const emptyBook = { title: "", slug: "", cover: "", preview_image: "", description: "", category: "Guide", pdf_url: "", published: false };

export function LibraryPanel() {
  const [books, setBooks] = useState([]);
  const [downloads, setDownloads] = useState([]);
  const [sel, setSel] = useState(null); // null=lista | "new" | book
  const [form, setForm] = useState(emptyBook);
  const [busy, setBusy] = useState(false);
  const [view, setView] = useState("books"); // books | downloads
  const pdfRef = useRef(null);

  const load = async () => {
    try {
      const [b, d] = await Promise.all([api.get("/admin/library/books"), api.get("/admin/library/downloads")]);
      setBooks(b.data); setDownloads(d.data);
    } catch { toast.error("Errore caricamento Library"); }
  };
  useEffect(() => { load(); }, []);

  const open = (b) => { setSel(b); setForm(b ? { ...emptyBook, ...b } : emptyBook); };

  const uploadPdf = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type !== "application/pdf") { toast.error("Carica un file PDF"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, pdf_url: data.url }));
      toast.success("PDF caricato");
    } catch (err) { toast.error(err.response?.data?.detail || "Errore upload PDF"); }
    finally { setBusy(false); if (pdfRef.current) pdfRef.current.value = ""; }
  };

  const save = async () => {
    if (!form.title.trim()) { toast.error("Il titolo è obbligatorio"); return; }
    setBusy(true);
    try {
      if (sel && sel !== "new") {
        await api.put(`/admin/library/books/${sel.id}`, form);
        toast.success("Guida aggiornata");
      } else {
        await api.post("/admin/library/books", form);
        toast.success("Guida creata");
      }
      setSel(null); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Errore salvataggio"); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm("Eliminare questa guida? I download già registrati restano nel CRM.")) return;
    try { await api.delete(`/admin/library/books/${sel.id}`); toast.success("Guida eliminata"); setSel(null); load(); }
    catch { toast.error("Errore eliminazione"); }
  };

  const btn = "min-h-[44px] inline-flex items-center gap-2 text-xs tracking-[0.14em] uppercase px-4 py-2 transition-colors disabled:opacity-60";

  if (sel) {
    return (
      <div className="space-y-6" data-testid="library-edit">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <button onClick={() => setSel(null)} className={`${btn} border border-ink hover:bg-ink hover:text-bone`} data-testid="book-back"><ArrowLeft size={14} /> Tutte le guide</button>
          {sel !== "new" && (
            <button onClick={remove} className={`${btn} border border-red-700 text-red-700 hover:bg-red-700 hover:text-bone`} data-testid="book-delete"><Trash2 size={14} /> Elimina</button>
          )}
        </div>
        <div className="bg-white border border-hair p-5 md:p-6 space-y-5">
          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">Titolo *</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="rounded-none mt-1" data-testid="book-title" /></div>
            <div><Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">Categoria (crea il segmento CRM)</Label>
              <Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="es. Ristrutturazione, Illuminazione" className="rounded-none mt-1" data-testid="book-category" /></div>
          </div>
          <div><Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">Descrizione</Label>
            <Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-none mt-1" data-testid="book-description" /></div>
          <div>
            <Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">Copertina</Label>
            <div className="mt-2"><ImageField value={form.cover} onChange={(v) => setForm({ ...form, cover: v })} testId="book-cover" /></div>
          </div>
          <div>
            <Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">PDF della guida *</Label>
            <div className="mt-2 flex items-center gap-3 flex-wrap">
              <button type="button" onClick={() => pdfRef.current?.click()} disabled={busy} className={`${btn} border border-ink hover:bg-ink hover:text-bone`} data-testid="book-upload-pdf">
                <Upload size={14} /> {form.pdf_url ? "Sostituisci PDF" : "Carica PDF"}
              </button>
              <input ref={pdfRef} type="file" accept="application/pdf" className="hidden" onChange={uploadPdf} data-testid="book-pdf-input" />
              {form.pdf_url && <a href={resolveMedia(form.pdf_url)} target="_blank" rel="noopener" className="text-xs text-gold underline" data-testid="book-pdf-link">PDF caricato — apri</a>}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Switch checked={!!form.published} onCheckedChange={(v) => setForm({ ...form, published: v })} data-testid="book-published" />
            <span className="text-sm">Pubblicato nella Library</span>
          </div>
          <button onClick={save} disabled={busy} className={`${btn} bg-ink text-bone hover:bg-gold`} data-testid="book-save"><Save size={14} /> Salva guida</button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="library-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex gap-2">
          <button onClick={() => setView("books")} className={`${btn} ${view === "books" ? "bg-ink text-bone" : "border border-hair hover:border-ink"}`} data-testid="library-view-books">Guide ({books.length})</button>
          <button onClick={() => setView("downloads")} className={`${btn} ${view === "downloads" ? "bg-ink text-bone" : "border border-hair hover:border-ink"}`} data-testid="library-view-downloads">Download ({downloads.length})</button>
        </div>
        <button onClick={() => open("new")} className={`${btn} bg-ink text-bone hover:bg-gold`} data-testid="book-new"><Plus size={14} /> Nuova guida</button>
      </div>

      {view === "books" ? (
        <div className="space-y-3" data-testid="library-list">
          {books.length === 0 && <div className="p-12 text-center text-taupe bg-white border border-hair">Nessuna guida. Crea la prima con "Nuova guida".</div>}
          {books.map((b) => (
            <button key={b.id} onClick={() => open(b)} className="w-full text-left bg-white border border-hair p-4 md:p-5 hover:border-gold transition-colors flex gap-4 items-center" data-testid={`book-row-${b.slug}`}>
              {b.cover ? <img src={resolveMedia(b.cover)} alt="" className="w-14 h-14 object-cover shrink-0" /> : <div className="w-14 h-14 bg-ink text-gold flex items-center justify-center shrink-0"><Download size={16} /></div>}
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-serif text-lg">{b.title}</span>
                  <span className={`text-[10px] tracking-[0.14em] uppercase border px-2 py-0.5 ${b.published ? "text-emerald-700 border-emerald-700/40" : "text-amber-700 border-amber-700/40"}`}>{b.published ? "Pubblicato" : "Bozza"}</span>
                  {!b.pdf_url && <span className="text-[10px] tracking-[0.14em] uppercase border border-red-700/40 text-red-700 px-2 py-0.5">PDF mancante</span>}
                </div>
                <p className="text-sm text-taupe mt-1 truncate">{b.category} · {b.downloads_count || 0} download</p>
              </div>
              <Pencil size={15} className="text-taupe shrink-0" />
            </button>
          ))}
        </div>
      ) : (
        <div className="space-y-3" data-testid="library-downloads">
          {downloads.length === 0 && <div className="p-12 text-center text-taupe bg-white border border-hair">Nessun download registrato.</div>}
          {downloads.map((d) => (
            <div key={d.id} className="bg-white border border-hair p-4 md:p-5" data-testid={`download-row-${d.id}`}>
              <div className="flex justify-between items-start gap-3 flex-wrap">
                <div className="min-w-0">
                  <span className="font-medium text-sm">{d.name}</span> <span className="text-sm text-taupe">· {d.email}</span>
                  <p className="text-sm text-taupe mt-1">“{d.book_title}” · {d.book_category}
                    {d.marketing_consent
                      ? <span className="text-emerald-700"> · consenso marketing</span>
                      : <span className="text-taupe"> · no marketing</span>}
                  </p>
                </div>
                <p className="text-xs text-taupe shrink-0">{fmtDate(d.downloaded_at)}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
